import { Box } from "@mui-wrapper";
import { useState } from "react";
import { SearchBar } from "@flat-ui";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { MainPageEvents } from "@utils/track-event/track-event-list";
import { getSearchQueryFromInput } from "@utils/search-utils";

interface Props {
  /** Callback when user executes a search */
  onSearchExecution(query: string): void;
}

/**
 * Simple input field that will execute the current value when user hits Enter key
 * But will only do so if input is not empty
 * If the value is a URL that contains an ID, only the ID is searched
 */
export function HomepageSearchBar({
  onSearchExecution,
}: Props): React.JSX.Element {
  const { trackEvent } = useTrackEvent();

  // local state to manage the input value of textfield. It is different than the search query which server responds to
  const [searchInput, setSearchInput] = useState<string>("");

  /** Handle when user presses Enter key */
  function handleOnKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter") {
      const searchQuery = getSearchQueryFromInput(searchInput);
      setSearchInput(searchQuery);
      trackEvent({
        name: MainPageEvents.startSearch,
        props: { searchLength: searchQuery.length },
      });
      onSearchExecution(searchQuery);
    }
  }

  /** Handle when user clicks the clear search icon */
  function onClearSearch(): void {
    trackEvent({
      name: MainPageEvents.clearSearch,
    });
    setSearchInput("");
    onSearchExecution("");
  }

  return (
    <Box sx={{ mx: 2.5 }}>
      <SearchBar
        autoFocus
        fullWidth
        placeholder="Search for workspaces, members or projects"
        text={searchInput}
        inputProps={{ "aria-label": "search", autoComplete: "off" }}
        onTextChanged={setSearchInput}
        onKeyDown={handleOnKeyDown}
        onSearchReset={onClearSearch}
        variant="underlined"
        sx={{ "& input": { fontSize: "1rem" } }}
      />
    </Box>
  );
}
